import React from "react";
import { Tooltip } from "antd";
import { AN_COLORS, AN_MONO } from "../../../design-system/tokens";
import { card } from "../ssh-proxy/sshProxyUi";

// The SSH access activity chart, shared by PAM → Auth Logs and the PAM
// dashboard. `timeline` is [{ tsUnixMs, approved, denied, refused, noAnswer,
// failed }], oldest first; `bucketMs` is each bar's width (it picks the tick
// format).

const HOUR = 60 * 60 * 1000;

// Chart colours: green approved; red denied + refused; amber no answer + failed.
// Outcome colours for access charts: green approved, red denied or refused,
// slate for no answer or failed. Used by the dashboard and the SSH access log
// (chart, legend and the count swatches) — change them here only.
// Red and green are hard to tell apart with red–green colour blindness, so
// the legend, tooltips and the gaps between stacked segments carry the
// meaning too; keep those.
export const OUTCOME_COLORS = {
  approved: "#16A34A",
  denied: "#DC2626",
  other: "#64748B",
};

const SERIES = [
  { key: "approved", label: "Approved", color: OUTCOME_COLORS.approved, of: (b) => b.approved || 0 },
  { key: "denied", label: "Denied or refused", color: OUTCOME_COLORS.denied, of: (b) => (b.denied || 0) + (b.refused || 0) },
  { key: "warn", label: "No answer or failed", color: OUTCOME_COLORS.other, of: (b) => (b.noAnswer || 0) + (b.failed || 0) },
];

// Stacked bars, one per bucket. Plain markup rather than a chart library: a
// few dozen bars, and the colours stay the design tokens.
export default function ActivityChart({ timeline, bucketMs, loading }) {
  const bars = timeline.map((b) => ({ ...b, parts: SERIES.map((s) => ({ ...s, n: s.of(b) })) }));
  const max = Math.max(1, ...bars.map((b) => b.parts.reduce((a, p) => a + p.n, 0)));
  const empty = !bars.some((b) => b.parts.some((p) => p.n));
  const tick = (ms) =>
    new Date(ms).toLocaleString("en-US", bucketMs >= 2 * HOUR ? { month: "short", day: "numeric" } : { hour: "2-digit", minute: "2-digit", hour12: false });
  const ticks = bars.length ? [bars[0], bars[Math.floor(bars.length / 2)], bars[bars.length - 1]] : [];

  return (
    <div style={{ ...card, padding: 16, marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontFamily: AN_MONO, fontSize: 10.5, letterSpacing: 1.1, textTransform: "uppercase", color: AN_COLORS.muted }}>
          Access activity
        </span>
        <span style={{ display: "inline-flex", gap: 14, fontSize: 12, color: AN_COLORS.ink2 }}>
          {SERIES.map((s) => (
            <span key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 9, height: 9, borderRadius: 2, background: s.color }} />
              {s.label}
            </span>
          ))}
        </span>
      </div>
      {empty ? (
        <div style={{ height: 160, display: "flex", alignItems: "center", justifyContent: "center", color: AN_COLORS.muted, fontSize: 13 }}>
          {loading ? "Loading…" : "No access attempts in this window."}
        </div>
      ) : (
        <>
          <div style={{ height: 160, display: "flex", alignItems: "flex-end", gap: 2 }}>
            {bars.map((b) => {
              const total = b.parts.reduce((a, p) => a + p.n, 0);
              return (
                <Tooltip
                  key={b.tsUnixMs}
                  title={
                    <div>
                      <div>{new Date(b.tsUnixMs).toLocaleString()}</div>
                      {b.parts.map((p) => (
                        <div key={p.key}>{p.label}: {p.n}</div>
                      ))}
                    </div>
                  }
                >
                  {/* Stacked segments with a 2px gap between them; the top
                      segment carries the rounded data-end. */}
                  <div style={{ flex: 1, minWidth: 2, height: `${(total / max) * 100}%`, display: "flex", flexDirection: "column-reverse", gap: 2 }}>
                    {(() => {
                      const shown = b.parts.filter((p) => p.n);
                      return shown.map((p, i) => (
                        <div
                          key={p.key}
                          style={{
                            flexGrow: p.n,
                            flexBasis: 0,
                            minHeight: 2,
                            background: p.color,
                            borderRadius: i === shown.length - 1 ? "4px 4px 0 0" : 0,
                          }}
                        />
                      ));
                    })()}
                  </div>
                </Tooltip>
              );
            })}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontFamily: AN_MONO, fontSize: 10.5, color: AN_COLORS.muted }}>
            {ticks.map((b, i) => (
              <span key={i}>{tick(b.tsUnixMs)}</span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
